// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

import * as React from 'react';

import styles from './BuildLoopsPlaySwitch.module.css';

/** The three MVP canvas views (concept 01/03): Build, Loops, Play. */
export type ViewMode = 'build' | 'loops' | 'play';

interface BuildLoopsPlaySwitchProps {
  /** The active view. */
  mode: ViewMode;
  /** Called with the new view when the user picks a different one. */
  onChange: (mode: ViewMode) => void;
}

const MODES: ReadonlyArray<{ id: ViewMode; label: string }> = [
  { id: 'build', label: 'Build' },
  { id: 'loops', label: 'Loops' },
  // The 'play' view id is kept; only the display label changed to "Test"
  // (Williams 2026-08-07) to avoid churning the mode id across the app.
  { id: 'play', label: 'Test' },
];

/**
 * The Build / Loops / Play view switcher (MVP WS2, concept 01/03) — the top-nav
 * that moves between the three canvas views on one shared model: Build (edit
 * structure), Loops (causal-loop view with the interactive loop highlight), and
 * Play (parameter sliders + Results band). The canvas stays put; only the
 * surrounding surfaces change.
 *
 * Presentation-only, like RenderModeToggle: `mode` is a prop and changes flow
 * back through `onChange`, so the Editor stays the single source of truth.
 */
export class BuildLoopsPlaySwitch extends React.PureComponent<BuildLoopsPlaySwitchProps> {
  render(): React.ReactNode {
    const { mode, onChange } = this.props;
    return (
      <div className={styles.switch} role="group" aria-label="Canvas view">
        {MODES.map((m) => (
          <button
            key={m.id}
            type="button"
            className={styles.option}
            aria-pressed={mode === m.id}
            onClick={() => {
              if (mode !== m.id) {
                onChange(m.id);
              }
            }}
          >
            {m.label}
          </button>
        ))}
      </div>
    );
  }
}
